import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeft, MessageSquareReply } from "lucide-react";
import { useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import type { FeedbackAdminList, FeedbackItem, FeedbackKind, FeedbackSeverity, FeedbackStatus } from "../api/types";
import { useEnvironmentValue } from "../app/environment";
import { Callout } from "../components/Callout";
import { Dialog } from "../components/Dialog";
import { EmptyState } from "../components/EmptyState";
import { PropRow } from "../components/panel";
import { Badge, Button, Field, IconButton, Segmented, textareaClass } from "../components/ui";
import { useMediaQuery } from "../components/useMediaQuery";
import { Waiting } from "../components/Waiting";
import { t, tServer } from "../i18n/i18n";
import { formatDateTime } from "../licence/licence";
import {
  contextRows,
  FEEDBACK_ADMIN_KEY,
  FEEDBACK_KEY,
  KIND_LABELS,
  REPLY_MAX,
  SEVERITY_LABELS,
  STATUS_LABELS,
} from "./feedbackModel";
import { CONTEXT_LABELS } from "./FeedbackDialog";
import { KindIcon, StatusBadge } from "./MyFeedbackDialog";

type All = "all";

/** Retours des utilisateurs, administration (registre F39) : tous les retours de toutes les licences de cet
 *  environnement, filtrés par type, état et gravité ; à droite (dessous sur un écran étroit) le retour choisi, sa
 *  capture et son contexte, son état et la réponse de l'équipe, lue par l'utilisateur dans « Mes retours ». Ouverte
 *  seulement pour une licence d'administration (le serveur répond 404 à toute autre). */
export function FeedbackAdminDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const environment = useEnvironmentValue((s) => s.environment);
  const wide = useMediaQuery("(min-width: 900px)");
  const [kind, setKind] = useState<FeedbackKind | All>("all");
  const [status, setStatus] = useState<FeedbackStatus | All>("new");
  const [severity, setSeverity] = useState<FeedbackSeverity | All>("all");
  const [chosen, setChosen] = useState<string | null>(null);
  const filters = {
    kind: kind === "all" ? null : kind,
    status: status === "all" ? null : status,
    severity: severity === "all" ? null : severity,
  };
  const list = useQuery<FeedbackAdminList>({
    queryKey: [...FEEDBACK_ADMIN_KEY, filters],
    queryFn: () => api.feedback.adminList(filters),
    staleTime: 0,
  });
  const items = list.data?.items ?? [];
  const counts = list.data?.counts;
  const current = items.find((i) => i.id === chosen) ?? (wide ? items[0] : undefined) ?? null;
  const showList = wide || !current;
  const showDetail = Boolean(current) && (wide || chosen !== null);

  return (
    <Dialog
      title={t("Retours des utilisateurs")}
      subtitle={
        (environment === "preprod" ? t("Préproduction") : environment === "dev" ? t("Développement") : t("Production")) +
        " · " +
        t("Nouveaux : {n} · pris en compte : {a} · livrés : {d} · refusés : {r}", {
          n: counts?.new ?? 0,
          a: counts?.acknowledged ?? 0,
          d: counts?.delivered ?? 0,
          r: counts?.declined ?? 0,
        })
      }
      onClose={() => {
        void queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
        onClose();
      }}
      width={1040}
    >
      <div className="space-y-2 border-b border-line px-4 py-3">
        <div className="flex flex-wrap gap-2">
          <Segmented<FeedbackStatus | All>
            ariaLabel={t("État")}
            fill={false}
            size="sm"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setChosen(null);
            }}
            options={[
              { value: "all", label: t("Tous") },
              ...(["new", "acknowledged", "delivered", "declined"] as const).map((s) => ({
                value: s,
                label: t(STATUS_LABELS[s]),
                count: s === "new" ? counts?.new || undefined : undefined,
              })),
            ]}
          />
          <Segmented<FeedbackKind | All>
            ariaLabel={t("Type de retour")}
            fill={false}
            size="sm"
            value={kind}
            onChange={(v) => {
              setKind(v);
              setChosen(null);
            }}
            options={[
              { value: "all", label: t("Tous les types") },
              { value: "evolution", label: t(KIND_LABELS.evolution) },
              { value: "anomaly", label: t(KIND_LABELS.anomaly) },
            ]}
          />
          <Segmented<FeedbackSeverity | All>
            ariaLabel={t("Gravité")}
            fill={false}
            size="sm"
            value={severity}
            onChange={(v) => {
              setSeverity(v);
              setChosen(null);
            }}
            options={[
              { value: "all", label: t("Toutes gravités") },
              { value: "blocking", label: t(SEVERITY_LABELS.blocking) },
              { value: "annoying", label: t(SEVERITY_LABELS.annoying) },
            ]}
          />
        </div>
      </div>
      {list.isError ? (
        <div className="p-4">
          <Callout tone="danger">{t("Les retours n'ont pas pu être lus : réessayez dans un instant.")}</Callout>
        </div>
      ) : !list.data ? (
        <EmptyState variant="loading" title={t("Chargement…")} compact />
      ) : items.length === 0 ? (
        <EmptyState icon={MessageSquareReply} title={t("Aucun retour pour ces filtres")} compact />
      ) : (
        <div className={clsx("min-h-0", wide && "grid grid-cols-[minmax(0,360px)_minmax(0,1fr)]")}>
          {showList && (
            <ul className={clsx("divide-y divide-line", wide && "border-r border-line")}>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setChosen(item.id)}
                    aria-current={current?.id === item.id || undefined}
                    className={clsx(
                      "flex w-full items-start gap-2 px-4 py-2.5 text-left transition-colors hover:bg-hover",
                      wide && current?.id === item.id && "bg-selected",
                    )}
                  >
                    <span className="mt-0.5 flex shrink-0">
                      <KindIcon item={item} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-ui leading-snug font-medium break-words text-ink">{item.title}</span>
                      <span className="block text-meta leading-snug text-ink-muted">
                        {t("N° {n}", { n: item.number })} · {formatDateTime(item.created_at)}
                        {item.seat_name ? ` · ${item.seat_name}` : ""}
                      </span>
                    </span>
                    <StatusBadge item={item} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showDetail && current && (
            <FeedbackDetail key={current.id} item={current} onBack={wide ? undefined : () => setChosen(null)} />
          )}
        </div>
      )}
    </Dialog>
  );
}

function FeedbackDetail({ item, onBack }: { item: FeedbackItem; onBack?: () => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<FeedbackStatus>(item.status);
  const [reply, setReply] = useState(item.reply ?? "");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);
  const changed = status !== item.status || reply.trim() !== (item.reply ?? "");
  const save = useMutation({
    mutationFn: () => api.feedback.adminUpdate(item.id, { status, reply: reply.trim() }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_ADMIN_KEY });
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
    },
  });
  const rows = contextRows(item.context, CONTEXT_LABELS);
  const meta: { label: string; value: string | null | undefined }[] = [
    { label: t("Envoyé le"), value: formatDateTime(item.created_at) },
    { label: t("Par"), value: item.author },
    { label: t("Poste"), value: item.seat_name !== item.author ? item.seat_name : null },
    { label: t("Licence"), value: [item.licence_type, item.space].filter(Boolean).join(" · ") },
    { label: t("Version du serveur"), value: item.context.build },
  ];
  return (
    <article className="min-w-0 space-y-4 px-4 py-3">
      <header className="flex items-start gap-2">
        {onBack && (
          <IconButton size="sm" onClick={onBack} title={t("Retour à la liste")} aria-label={t("Retour à la liste")}>
            <ArrowLeft size={16} />
          </IconButton>
        )}
        <span className="mt-1 flex shrink-0">
          <KindIcon item={item} size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-title leading-snug font-semibold break-words text-ink">{item.title}</h3>
          <p className="text-meta text-ink-muted">
            {t("N° {n}", { n: item.number })} · {t(KIND_LABELS[item.kind])}
            {item.severity ? ` · ${t(SEVERITY_LABELS[item.severity])}` : ""}
          </p>
        </div>
        {item.severity === "blocking" && (
          <Badge tone="danger" dot>
            {t(SEVERITY_LABELS.blocking)}
          </Badge>
        )}
      </header>

      {item.kind === "anomaly" && (
        <>
          <Text label={t("Ce que l'utilisateur faisait")} value={item.doing} />
          <Text label={t("Ce qui s'est passé")} value={item.happened} />
        </>
      )}
      <Text label={item.kind === "anomaly" ? t("Précisions") : t("Description")} value={item.description} />

      {item.has_screenshot && (
        <a href={api.feedback.adminScreenshotUrl(item.id)} target="_blank" rel="noreferrer" className="block" title={t("Ouvrir la capture en grand")}>
          <img
            src={api.feedback.adminScreenshotUrl(item.id)}
            alt={t("Capture de l'écran jointe")}
            className="max-h-72 w-full rounded-md border border-line bg-canvas object-contain"
          />
        </a>
      )}

      <div className="rounded-lg border border-line px-3 py-1">
        {meta
          .filter((m) => m.value)
          .map((m) => (
            <PropRow key={m.label} label={m.label}>
              {m.value}
            </PropRow>
          ))}
        {rows.map((row) => (
          <PropRow key={row.label} label={row.label}>
            {row.value}
          </PropRow>
        ))}
      </div>

      <div className="space-y-3 rounded-lg border border-line px-3 py-3">
        <Segmented<FeedbackStatus>
          label={t("État")}
          size="sm"
          value={status}
          onChange={setStatus}
          options={(["new", "acknowledged", "delivered", "declined"] as const).map((s) => ({ value: s, label: t(STATUS_LABELS[s]) }))}
        />
        <Field label={t("Réponse à l'utilisateur")} hint={t("Lue dans « Mes retours » ; vide : pas de réponse.")}>
          <textarea
            className={clsx(textareaClass, "min-h-24")}
            value={reply}
            maxLength={REPLY_MAX}
            onChange={(e) => setReply(e.target.value)}
          />
        </Field>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {save.isError && (
            <span className="mr-auto text-meta text-danger">
              {save.error instanceof ApiError ? tServer(save.error.message) : t("Enregistrement impossible : réessayez.")}
            </span>
          )}
          {saved && !changed && <span className="mr-auto text-meta text-ok">{t("Enregistré")}</span>}
          <Button size="sm" disabled={!changed || save.isPending} onClick={() => save.mutate()} highlight={changed && !save.isPending}>
            {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer")}
          </Button>
        </div>
      </div>
    </article>
  );
}

function Text({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <section>
      <h4 className="mb-0.5 text-meta font-medium text-ink-muted">{label}</h4>
      <p className="text-ui leading-relaxed break-words whitespace-pre-wrap text-ink">{value}</p>
    </section>
  );
}
